import { Component, Input, OnChanges } from '@angular/core';
import { ITealiumAnalyticsOptions, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ComponentData, DisplayDataFactoryService, TabData, TableData } from '@ddt-web/data-service';
import { ResponsiveDisplayConstants } from '@ddt-web/dom';
import * as get from 'lodash/get';

import { NestedTableConfig } from '../models';
import { DisplayTableColumnClassOverride } from '../models/display-table-column-class-override.interface';

@Component({
	selector: 'dl-display-table',
	templateUrl: './display-table.component.html',
	styleUrls: ['./display-table.component.scss']
})
export class DisplayTableComponent implements OnChanges {
	@Input() public data: ComponentData | TabData;
	@Input() public total?: ComponentData | TabData;
	@Input() public bordered: boolean = false;
	@Input() public padded: boolean = false;
	@Input() public striped: boolean = false;
	@Input('parentCollapsed') public parentCollapsed: boolean = false;
	@Input('transpose-in-mobile') public isTransposedInMobile: boolean = false;
	@Input() public hideRows: boolean = false;
	@Input() public colors: Array<string>;
	@Input() public columnClassOverride?: DisplayTableColumnClassOverride;
	@Input() public tableOptions?: NestedTableConfig;

	// formatters are intended to be used if you cannot provide the complete formatted value from an extractor
	// e.g. if you are using template strings
	@Input() public headersXFormatter: any = { formatter: (header: string) => header };
	@Input() public tooltipFormatter: any = { formatter: (tooltip: string) => tooltip };

	public categoryColumnClass: string;
	public dataColumnClass: string;
	public expandIconColumnClass: string;
	public iconColumnClass: string;

	public tabData: TabData;
	public tableData: TableData;
	public totalData: TableData;
	public hasChildRows: boolean = false;
	public riskInfoBubbleText: string;
	public showRiskInfoBubbleModal: boolean = false;

	constructor(private displayDataFactoryService: DisplayDataFactoryService, private tealiumAnalyticsService: TealiumAnalyticsService) { }

	public ngOnChanges(): void {
		if (this.data instanceof ComponentData) {
			this.tabData = this.data && this.data.tabs && this.data.tabs[0];
		} else {
			this.tabData = this.data;
		}
		this.tableData = get(this.tabData, 'tableData[0]');
		if (get(this.tableData, 'headersY', []).find(head => !!(head.children?.length))) {
			this.hasChildRows = true;
		}
		this.transposeTable();
		this.initializeTotalRow();
		const filteredHeaders = get(this.tableData, 'headersX', []).filter(header => get(header, 'config.visible', true));
		this.assignColumnClasses(get(filteredHeaders, 'length', 0), this.columnClassOverride);

		// TODO: This is a hack for ACHO-3105, need to revisit this later
		if (get(this.tabData, 'id') === 'total.risk.characteristics') {
			this.riskInfoBubbleText = get(this.tableData, 'headersX[0].config.infoBubble', '').toString();
		}
	}

	private initializeTotalRow(): void {
		if (this.total) {
			if (this.total instanceof ComponentData) {
				this.totalData = get(this.total, 'tabs[0].tableData[0]');
			} else {
				this.totalData = get(this.total, 'tableData[0]');
			}
		}
	}

	private assignColumnClasses(index: number, columnClassOverride?: DisplayTableColumnClassOverride): void {
		if (columnClassOverride) {
			this.categoryColumnClass = columnClassOverride.categoryColumnClass;
			this.dataColumnClass = columnClassOverride.dataColumnClass;
			this.expandIconColumnClass = columnClassOverride.expandIconColumnClass;
			this.iconColumnClass = columnClassOverride.iconColumnClass;
		} else if (this.hideRows) {
			this.categoryColumnClass = 'col-24';
			this.dataColumnClass = 'col-24';
		} else {
			switch (index) {
				case 1:
					this.categoryColumnClass = this.hasChildRows ? 'col-11 col-sm-18' : 'col-12 col-sm-16';
					this.dataColumnClass = 'col-12 col-sm-8';
					break;
				case 2:
					this.categoryColumnClass = this.hasChildRows ? 'col-11 col-sm-24' : 'col-12 col-sm-24';
					this.dataColumnClass = 'col-6 col-sm-12';
					break;
				case 3:
					this.categoryColumnClass = this.hasChildRows ? 'col-8 col-sm-24' : 'col-9 col-sm-24';
					this.dataColumnClass = 'col-5 col-sm-8';
					break;
				case 4:
					this.categoryColumnClass = this.hasChildRows ? 'col-7 col-sm-24' : 'col-8 col-sm-24';
					this.dataColumnClass = 'col-4 col-sm-6';
					break;
				case 5:
					this.categoryColumnClass = this.hasChildRows ? 'col-6 col-sm-24' : 'col-9 col-sm-24';
					this.dataColumnClass = 'col-3 col-sm-one-fifth';
					break;
			}
		}
	}

	private transposeTable(): void {
		if (this.isTransposedInMobile && window.innerWidth <= ResponsiveDisplayConstants.DEVICE_WIDTH.MOBILE_MAX && this.tableData) {
			const tableData = this.displayDataFactoryService.transpose(this.tabData.tableData);
			this.tableData = tableData && tableData.length ? tableData[0] : this.tableData;
		}
	}

	public toggleInfoDialog(): void {
		const infoBubbleId = get(this.tableData, 'headersX[0].config.infoBubble.key');
		if (infoBubbleId) {
			const analyticData: ITealiumAnalyticsOptions = { linkName: 'tooltip_' + infoBubbleId };
			const event = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.TOOL_TIP, analyticData);
			this.tealiumAnalyticsService.trackEventTealium(event);
		}
		this.showRiskInfoBubbleModal = !this.showRiskInfoBubbleModal;
	}
}
